import React, {useCallback, useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import {createEmployeeSignRequest, getConsentSignerRoleLabel} from "@source/api/models/consentHelpers";
import {ConsentCategorySource, ConsentDocument, ConsentDocumentStatus, EMPLOYEE_CONSENT_STATEMENT} from "@source/api/models/consentModels";
import Button from "@source/components/UI/buttons/button/button";
import Checkbox from "@source/components/UI/buttons/checkbox/checkbox";
import CacheImg from "@source/components/UI/cacheImg/cacheImg";
import userStore from "@source/store/userStore";
import {HOME_ROUTES, stringToHslColor} from "@source/utils/utils";
import {ConsentDocumentPreview} from "@source/components/consentDocuments/consentDocumentPreview";
import {formatConsentDate} from "@source/components/consentDocuments/consentDate";
import {ConsentRequestState, SignatoryProgress} from "@source/components/consentDocuments";
import {canCurrentEmployeeSign, getEmployeeSignSuccessTitle} from "@source/components/consentDocuments/employeeSigningHelpers";
import StepBackBtn from "@source/pages/HomePage/homeRouter/updateTab/steps/step/stepBackBtn/stepBackBtn";
import styles from "./employeeSigning.module.scss";

const unavailableMessage = (status?: number) => {
  if (status === 403) return "You are no longer authorized to review or sign this document.";
  if (status === 404) return "This consent document is no longer available.";
  if (status === 409) return "This document has already been signed or its signing stage has advanced.";
  if (status === 400) return "Your acknowledgement could not be accepted. Review the document and try again.";
  return "Unable to complete your signature. Please try again.";
};

export const EmployeeSigning = () => {
  const {documentId} = useParams<{documentId: string}>();
  const history = useHistory();
  const [document, setDocument] = useState<ConsentDocument>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [signing, setSigning] = useState(false);
  const [result, setResult] = useState<"completed" | "waiting">();
  const [unavailable, setUnavailable] = useState("");
  const [terminalFailure, setTerminalFailure] = useState<403 | 404 | 409>();
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const employeeId = userStore.getId() || "";

  const load = useCallback(async () => {
    if (!documentId) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    setUnavailable("");
    setTerminalFailure(undefined);
    setResult(undefined);
    setAcknowledged(false);
    setPreviewReady(false);
    try {
      const response = await ConsentController.getEmployeeDocument(documentId);
      if (mountedRef.current && requestId === requestIdRef.current) setDocument(response);
    } catch (requestError: unknown) {
      if (mountedRef.current && requestId === requestIdRef.current) {
        const status = requestError instanceof ReqError ? requestError.status : undefined;
        setError(unavailableMessage(status));
      }
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [load]);

  const isActiveAssignee = document
    ? canCurrentEmployeeSign(document, employeeId)
    : false;
  const isFinalizing = Boolean(document
    && document.status === ConsentDocumentStatus.AWAITING_SIGNATURES
    && Array.isArray(document.activeStages)
    && document.activeStages.length === 0);
  const sign = async () => {
    if (!document || !documentId || !acknowledged || !previewReady || !isActiveAssignee || signing || terminalFailure) return;
    setSigning(true);
    setUnavailable("");
    try {
      const response = await ConsentController.signEmployeeDocument(documentId, createEmployeeSignRequest());
      if (!mountedRef.current) return;
      setDocument(response);
      setResult(response.status === ConsentDocumentStatus.COMPLETED ? "completed" : "waiting");
    } catch (requestError: unknown) {
      if (!mountedRef.current) return;
      const status = requestError instanceof ReqError ? requestError.status : undefined;
      if (status === 403 || status === 404 || status === 409) {
        setAcknowledged(false);
        setPreviewReady(false);
        setTerminalFailure(status);
        setUnavailable(unavailableMessage(status));
      } else {
        setUnavailable(unavailableMessage(status));
      }
    } finally {
      if (mountedRef.current) setSigning(false);
    }
  };

  if (loading) return <ConsentRequestState variant="loading" title="Loading consent document"/>;
  if (error || !document) return <ConsentRequestState variant="error" title="Consent document unavailable" description={error || "This consent document is no longer available."} onRetry={() => void load()}/>;
  if (result) return <div className={styles.result}>
    <h2>{getEmployeeSignSuccessTitle(document.status)}</h2>
    <p>{result === "completed" ? "All required consent steps are complete." : "Your signature was recorded successfully."}</p>
    <Button event="link" link={HOME_ROUTES.ConsentDocumentsAwaitingYourSign}>Return to Awaiting Your Sign</Button>
  </div>;
  if (terminalFailure) return <div className={styles.result}>
    <h2>{terminalFailure === 409 ? "Signing no longer available" : "Signing unavailable"}</h2>
    <p>{unavailable}</p>
    {terminalFailure === 409 ? <Button variant="outlined" onClick={() => void load()}>Refresh document</Button> : null}
    <Button event="link" link={HOME_ROUTES.ConsentDocumentsAwaitingYourSign}>Return to Awaiting Your Sign</Button>
  </div>;

  const residentName = [document.resident.firstName, document.resident.lastName].filter(Boolean).join(" ") || "Resident unavailable";
  const residentInitials = `${document.resident.firstName?.[0] || ""}${document.resident.lastName?.[0] || ""}`.toUpperCase() || "R";
  const categoryName = document.categorySnapshot?.name || "Legacy consent document";
  const source = document.categorySnapshot?.source === ConsentCategorySource.RAW_PDF || document.source === ConsentCategorySource.RAW_PDF ? "Existing PDF" : "Template";
  const signingOrder = document.categorySnapshot?.signingOrderRequired
    ? (document.categorySnapshot.signingOrder || []).map((role) => getConsentSignerRoleLabel(role)).join(" → ") || "Signing order unavailable"
    : "Required signer roles may sign in parallel.";

  return <div className={styles.signing}>
    <section className={styles.details}>
      <div className={styles.residentSummary}>
        <CacheImg url={document.resident.photo} alt={`${residentName} avatar`} className={styles.photo} imgClassName={styles.photo} skeletonProps={{variant: "circular"}}>
          <div className={styles.noPhoto} style={{backgroundColor: stringToHslColor(`${document.resident.firstName || ""}${document.resident.lastName || ""}`)}}>{residentInitials}</div>
        </CacheImg>
        <div><h2>{residentName}</h2><p>{document.title || "Untitled consent document"}</p></div>
      </div>
      <StepBackBtn onClick={() => history.push(HOME_ROUTES.ConsentDocumentsAwaitingYourSign)} label="Back to consent documents" disabled={signing}/>
      <dl>
        <div><dt>Consent Category</dt><dd>{categoryName}</dd></div>
        <div><dt>Source</dt><dd>{source}</dd></div>
        <div><dt>Sent</dt><dd>{formatConsentDate(document.sentAt)}</dd></div>
        <div><dt>Nursing home</dt><dd>{document.nursingHome?.name || "Nursing home unavailable"}</dd></div>
        <div><dt>Signing order</dt><dd>{signingOrder}</dd></div>
      </dl>
      <section className={styles.progress}><h3>Signing progress</h3><SignatoryProgress compact signatories={document.signatories}/></section>
    </section>
    <section className={styles.review}>
      <h2>Review document</h2>
      <ConsentDocumentPreview className={styles.reviewPreview} documentId={document._id} onReadinessChange={setPreviewReady}/>
      {unavailable ? <div className={styles.submitError} role="alert">{unavailable}</div> : null}
      {isActiveAssignee ? <div className={styles.actions}>
        <Checkbox name="employee-consent-acknowledgement" value="confirmed" checked={acknowledged} disabled={signing || !previewReady} onChange={(event) => setAcknowledged(event.target.checked)} label={EMPLOYEE_CONSENT_STATEMENT}/>
        <Button onClick={() => void sign()} disabled={!acknowledged || !previewReady || signing} isLoading={signing}>Sign document</Button>
      </div> : <ConsentRequestState
        variant="empty"
        title={isFinalizing ? "Document finalizing" : "Signing unavailable"}
        description={isFinalizing
          ? "This document is finalizing. Return to Awaiting Your Sign and refresh for its current state."
          : "You are not an active employee signer for this document."}
      />}
    </section>
  </div>;
};
